/**
 * Map Drawing Controller - Manager for drawing operations
 * Delegates to specialized controllers for polygon, rectangle, and distance measurement
 */
import {Subject} from 'rxjs';
import {mergeParams} from '@app/utils/_msf';
import {DrawPolygonController} from './draw-polygon.controller';
import {DrawRectangleController} from './draw-rectangle.controller';
import {DrawDistanceController} from './draw-distance.controller';
import {DrawBoxPolygonController} from './draw-box-polygon.controller';
import {DrawCustomPolygonController} from './draw-custom-polygon.controller';
import {TranslateService} from '@ngx-translate/core';

export class MapDrawingController {
  private drawnItems: any;
  private map: any;
  private layersController: any = null;

  // Specialized controllers
  private polygonController: DrawPolygonController;
  private rectangleController: DrawRectangleController;
  private distanceController: DrawDistanceController;
  private boxPolygonController: DrawBoxPolygonController;
  private customPolygonController: DrawCustomPolygonController;

  // Drawing state
  private currentDrawingState: {
    isDrawing: boolean;
    drawingType: 'rectangle' | 'polygon' | 'distance' | 'box-polygon' | 'custom-polygon' | null;
  } = {
    isDrawing: false,
    drawingType: null
  };

  // Event emitters for shape creation
  public shapeCreated$ = new Subject<{
    type: 'polygon' | 'rectangle' | 'box-polygon' | 'custom-polygon';
    coordinates: [number, number][];
    center: { lat: number; lng: number };
    layer: any;
    area?: number;
  }>();

  // Event emitter for drawing completion (success or error)
  public drawingCompleted$ = new Subject<{
    success: boolean;
    error?: any;
    shape?: {
      type: 'polygon' | 'rectangle';
      coordinates: [number, number][];
      center: { lat: number; lng: number };
      layer: any;
    };
  }>();

  constructor(private mapInstance: any, private translateService:TranslateService) {
    this.map = mapInstance;

    // Initialize specialized controllers
    this.polygonController = new DrawPolygonController(this.map, this.translateService);
    this.rectangleController = new DrawRectangleController(this.map);
    this.distanceController = new DrawDistanceController(this.map, translateService);
    this.boxPolygonController = new DrawBoxPolygonController(this.map, translateService);
    this.customPolygonController = new DrawCustomPolygonController(this.map, translateService);

    // Set callback for distance controller to check if other drawing modes are active
    this.distanceController.setIsOtherDrawingModeActiveCallback(() => {
      return this.currentDrawingState.isDrawing &&
             this.currentDrawingState.drawingType !== 'distance' &&
             this.currentDrawingState.drawingType !== null;
    });

    // Subscribe to shape creation events from specialized controllers
    this.polygonController.shapeCreated$.subscribe(shape => {
      this.shapeCreated$.next(shape);
      // Register the shape and enable edit mode
      // this.polygonController.registerDrawnShape(shape.layer);
      this.polygonController.enableEditMode();
    });

    this.rectangleController.shapeCreated$.subscribe(shape => {
      this.shapeCreated$.next(shape);
      // Register the shape and enable edit mode
      // this.rectangleController.registerDrawnShape(shape.layer);
      this.rectangleController.enableEditMode();
    });

    this.boxPolygonController.shapeCreated$.subscribe(shape => {
      this.shapeCreated$.next(shape as any);
      // Register the shape and enable edit mode
      this.boxPolygonController.registerDrawnShape(shape.layer, [], []);
      this.boxPolygonController.enableEditMode();
    });

    this.customPolygonController.shapeCreated$.subscribe(shape => {
      this.shapeCreated$.next(shape as any);
      // Register the shape and enable edit mode
      // this.customPolygonController.registerDrawnShape(shape.layer, [], []);
      this.customPolygonController.enableEditMode();
    });
  }

  // =========================================================================
  // LAYERS CONTROLLER SETTER
  // =========================================================================

  /**
   * Set the layers controller reference for polygon interactivity control
   */
  public setLayersController(layersController: any): void {
    this.layersController = layersController;
  }

  // =========================================================================
  // PUBLIC DRAWING CONTROL METHODS
  // =========================================================================

  /**
   * Helper: Disable a specific drawing mode
   */
  private disableDrawingMode(mode: 'rectangle' | 'polygon' | 'distance' | 'box-polygon' | 'custom-polygon'): void {
    switch (mode) {
      case 'rectangle':
        this.rectangleController.disable();
        break;
      case 'polygon':
        this.polygonController.finishPolylineDrawing();
        this.polygonController.disable();
        break;
      case 'distance':
        this.distanceController.disable();
        break;
      case 'box-polygon':
        this.boxPolygonController.disable();
        break;
      case 'custom-polygon':
        this.customPolygonController.finishPolygonDrawing();
        this.customPolygonController.disable();
        break;
    }
    this.currentDrawingState.isDrawing = false;
    this.currentDrawingState.drawingType = null;
  }

  /**
   * Helper: Toggle off current drawing mode if it matches the specified type
   * @returns true if the mode was toggled off, false otherwise
   */
  private toggleOffIfActive(mode: 'rectangle' | 'polygon' | 'distance' | 'box-polygon' | 'custom-polygon'): boolean {
    if (this.currentDrawingState.drawingType === mode) {
      this.disableDrawingMode(mode);
      this.enablePolygonInteractivity();
      return true;
    }
    return false;
  }

  /**
   * Helper: Disable all other drawing modes except the specified one
   */
  private disableOtherDrawingModes(exceptMode: 'rectangle' | 'polygon' | 'distance' | 'box-polygon' | 'custom-polygon'): void {
    const modes: ('rectangle' | 'polygon' | 'distance' | 'box-polygon' | 'custom-polygon')[] =
      ['rectangle', 'polygon', 'distance', 'box-polygon', 'custom-polygon'];

    for (const mode of modes) {
      if (mode !== exceptMode && this.currentDrawingState.drawingType === mode) {
        this.disableDrawingMode(mode);
      }
    }
  }

  /**
   * Helper: Enable polygon interactivity
   */
  private enablePolygonInteractivity(): void {
    if (this.layersController && typeof this.layersController.enablePolygonInteractivity === 'function') {
      this.layersController.enablePolygonInteractivity();
    }
  }

  /**
   * Helper: Disable polygon interactivity
   */
  private disablePolygonInteractivity(): void {
    if (this.layersController && typeof this.layersController.disablePolygonInteractivity === 'function') {
      this.layersController.disablePolygonInteractivity();
    }
  }

  /**
   * Enable polygon drawing mode
   */
  public enablePolygonDrawing(): void {
    // If already in polygon mode, toggle off
    if (this.toggleOffIfActive('polygon')) {
      return;
    }

    // Disable all other drawing modes
    this.disableOtherDrawingModes('polygon');

    // Enable polygon drawing
    this.polygonController.enable();
    this.currentDrawingState.isDrawing = true;
    this.currentDrawingState.drawingType = 'polygon';

    // Disable polygon interactivity to allow drawing over regions
    this.disablePolygonInteractivity();
  }

  /**
   * Enable rectangle drawing mode
   */
  public enableRectangleDrawingMode(): void {
    // If already in rectangle mode, toggle off
    if (this.toggleOffIfActive('rectangle')) {
      return;
    }

    // Disable all other drawing modes
    this.disableOtherDrawingModes('rectangle');

    // Enable rectangle drawing
    this.rectangleController.enable();
    this.currentDrawingState.isDrawing = true;
    this.currentDrawingState.drawingType = 'rectangle';

    // Disable polygon interactivity to allow drawing over regions
    this.disablePolygonInteractivity();
  }

  /**
   * Enable distance measurement mode
   */
  public enableDistanceMeasurement(): void {
    // If already in distance mode, toggle off
    if (this.toggleOffIfActive('distance')) {
      return;
    }

    // Disable all other drawing modes
    this.disableOtherDrawingModes('distance');

    // Enable distance measurement
    this.distanceController.enable();
    this.currentDrawingState.isDrawing = true;
    this.currentDrawingState.drawingType = 'distance';

    // Disable polygon interactivity to allow clicking through them
    this.disablePolygonInteractivity();
  }

  /**
   * Check if rectangle drawing is active
   */
  public isDrawingRectangleActive(): boolean {
    return this.currentDrawingState.isDrawing && this.currentDrawingState.drawingType === 'rectangle';
  }

  /**
   * Check if polygon drawing is active
   */
  public isDrawingPolygonActive(): boolean {
    return this.currentDrawingState.isDrawing && this.currentDrawingState.drawingType === 'polygon';
  }

  /**
   * Get current drawing state
   */
  public getCurrentDrawingState(): { isDrawing: boolean; drawingType: 'rectangle' | 'polygon' | 'distance' | 'box-polygon' | 'custom-polygon' | null } {
    return {...this.currentDrawingState};
  }

  public setCurrentDrawingState(state: { isDrawing: boolean; drawingType: 'rectangle' | 'polygon' | 'distance' | 'box-polygon' | 'custom-polygon' | null }) {
    mergeParams(this.currentDrawingState, state);
  }

  /**
   * Check if rectangle drawing is active (low-level)
   */
  public isRectangleDrawingActive(): boolean {
    return this.rectangleController.isActive();
  }

  /**
   * Check if polygon drawing is active (low-level)
   */
  public isPolylineDrawingActive(): boolean {
    return this.polygonController.isActive();
  }

  /**
   * Check if distance measurement is active
   */
  public isDistanceMeasurementModeActive(): boolean {
    return this.distanceController.isActive();
  }

  /**
   * Toggle distance measurement mode
   */
  public toggleDistanceMeasurement(): void {
    this.enableDistanceMeasurement();
  }

  // =========================================================================
  // BOX POLYGON & CUSTOM POLYGON METHODS
  // =========================================================================

  /**
   * Enable box polygon drawing mode
   */
  public enableBoxPolygonDrawing(): void {
    // If already in box-polygon mode, toggle off
    if (this.toggleOffIfActive('box-polygon')) {
      return;
    }

    // Disable all other drawing modes
    this.disableOtherDrawingModes('box-polygon');

    // Enable box polygon drawing
    this.boxPolygonController.enable();
    this.currentDrawingState.isDrawing = true;
    this.currentDrawingState.drawingType = 'box-polygon';

    // Disable polygon interactivity to allow drawing over regions
    this.disablePolygonInteractivity();
  }

  /**
   * Enable custom polygon drawing mode
   */
  public enableCustomPolygonDrawing(): void {
    // If already in custom-polygon mode, toggle off
    if (this.toggleOffIfActive('custom-polygon')) {
      return;
    }

    // Disable all other drawing modes
    this.disableOtherDrawingModes('custom-polygon');

    // Enable custom polygon drawing
    this.customPolygonController.enable();
    this.currentDrawingState.isDrawing = true;
    this.currentDrawingState.drawingType = 'custom-polygon';

    // Disable polygon interactivity to allow drawing over regions
    this.disablePolygonInteractivity();
  }

  /**
   * Check if box polygon drawing is active
   */
  public isDrawingBoxPolygonActive(): boolean {
    return this.currentDrawingState.isDrawing && this.currentDrawingState.drawingType === 'box-polygon';
  }

  /**
   * Check if custom polygon drawing is active
   */
  public isDrawingCustomPolygonActive(): boolean {
    return this.currentDrawingState.isDrawing && this.currentDrawingState.drawingType === 'custom-polygon';
  }

  /**
   * Disable all drawing modes (helper method)
   */
  public disableAllDrawingModes(): void {
    if (this.currentDrawingState.drawingType === 'polygon') {
      this.polygonController.disable();
    }
    if (this.currentDrawingState.drawingType === 'rectangle') {
      this.rectangleController.disable();
    }
    if (this.currentDrawingState.drawingType === 'distance') {
      this.distanceController.disable();
    }
    if (this.currentDrawingState.drawingType === 'box-polygon') {
      this.boxPolygonController.disable();
    }
    if (this.currentDrawingState.drawingType === 'custom-polygon') {
      this.customPolygonController.disable();
    }

    this.currentDrawingState.isDrawing = false;
    this.currentDrawingState.drawingType = null;
  }

  // =========================================================================
  // LEGACY METHODS FOR BACKWARD COMPATIBILITY
  // =========================================================================

  /**
   * Enable rectangle drawing mode (legacy)
   */
  public enableRectangleDrawing(): void {
    this.enableRectangleDrawingMode();
  }

  /**
   * Disable rectangle drawing mode (legacy)
   */
  public disableRectangleDrawing(): void {
    this.rectangleController.disable();

    // Re-enable polygon interactivity
    if (this.layersController && typeof this.layersController.enablePolygonInteractivity === 'function') {
      this.layersController.enablePolygonInteractivity();
    }
  }

  /**
   * Enable polyline drawing mode (legacy)
   */
  public enablePolylineDrawing(): void {
    this.polygonController.enable();
  }

  /**
   * Disable polyline drawing mode (legacy)
   */
  public disablePolylineDrawing(): void {
    this.polygonController.disable();

    // Re-enable polygon interactivity
    if (this.layersController && typeof this.layersController.enablePolygonInteractivity === 'function') {
      this.layersController.enablePolygonInteractivity();
    }
  }

  /**
   * Disable distance measurement mode (legacy)
   */
  public disableDistanceMeasurement(): void {
    this.distanceController.disable();

    // Re-enable polygon interactivity
    if (this.layersController && typeof this.layersController.enablePolygonInteractivity === 'function') {
      this.layersController.enablePolygonInteractivity();
    }
  }

  /**
   * Finish polyline drawing (legacy)
   */
  public finishPolylineDrawing(): void {
    this.polygonController.finishPolylineDrawing();
  }

  /**
   * Get polyline points (legacy)
   */
  public getPolylinePoints(): any[] {
    return this.polygonController.getPolylinePoints();
  }

  // =========================================================================
  // SHAPES MANAGEMENT
  // =========================================================================

  /**
   * Set the drawn items feature group
   */
  public setDrawnItems(items: any): void {
    this.drawnItems = items;
    this.polygonController.setDrawnItems(items);
    this.rectangleController.setDrawnItems(items);
    this.boxPolygonController.setDrawnItems(items);
    this.customPolygonController.setDrawnItems(items);
  }

  /**
   * Get all drawn shapes
   */
  public getDrawnShapes(): any[] {
    const shapes: any[] = [];
    if (this.drawnItems) {
      let index = 0;
      this.drawnItems.eachLayer((layer: any) => {
        shapes.push({
          id: layer._leaflet_id || `shape_${index}`,
          type: layer instanceof (window as any).L.Rectangle ? 'rectangle' : 'polygon',
          layer: layer
        });
        index++;
      });
    }
    return shapes;
  }

  /**
   * Clear all drawn shapes (polygons, rectangles, distance measurements)
   */
  public clearAllDrawnShapes(): void {
    // Clear measurement markers from all layers before clearing the layers
    if (this.drawnItems) {
      this.drawnItems.eachLayer((layer: any) => {
        // Remove measurement markers if they exist
        if (layer._measurementMarkers && Array.isArray(layer._measurementMarkers)) {
          layer._measurementMarkers.forEach((marker: any) => {
            if (this.map && marker) {
              this.map.removeLayer(marker);
            }
          });
          layer._measurementMarkers = [];
        }
      });

      // Clear all drawn items from the map (this includes shapes, markers, and labels)
      this.drawnItems.clearLayers();
    }

    // Clear distance measurements
    this.distanceController.clearAllMeasurements();

    // Clear any stored shapes in specialized controllers
    this.boxPolygonController.clearAllShapes();
    this.customPolygonController.clearAllShapes();
  }

  /**
   * Undo last drawn shape (go back to previous state)
   */
  public undoLastDrawnShape(): void {
    if (this.drawnItems) {
      const layers = this.drawnItems.getLayers();
      if (layers.length > 0) {
        const lastLayer = layers[layers.length - 1];
        this.drawnItems.removeLayer(lastLayer);
      } else {
        return;
      }
    }
  }

  /**
   * Show/toggle measurements for all drawn polygons on the map
   * This will display area and perimeter measurements for existing polygons
   */
  public toggleMeasurementsForAllShapes(): void {
    if (!this.drawnItems) {
      return;
    }

    const layers = this.drawnItems.getLayers();
    if (layers.length === 0) {
      return;
    }

    // TODO: Implement measurement display logic for all shapes
    // This could create tooltips or labels showing area/perimeter for each shape
    layers.forEach((layer: any) => {
      if (layer instanceof (window as any).L.Polygon || layer instanceof (window as any).L.Rectangle) {
        this.showMeasurementForShape(layer);
      }
    });
  }

  /**
   * Show measurement for a specific shape
   */
  private showMeasurementForShape(layer: any): void {
    // Get the bounds and calculate area
    const bounds = layer.getBounds();
    const center = bounds.getCenter();

    // Calculate area (simplified - you may want to use proper geospatial calculations)
    const latlngs = layer.getLatLngs()[0];
    if (!latlngs || latlngs.length === 0) return;

    // Create measurement label
    const L = (window as any).L;
    const measurementHtml = `
      <div style="
        background: rgba(102, 126, 234, 0.9);
        color: white;
        padding: 6px 10px;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        font-family: 'Segoe UI', Arial, sans-serif;
        font-size: 12px;
        font-weight: 600;
        white-space: nowrap;
        pointer-events: none;
      ">
        📏 Measurement
      </div>
    `;

    const measurementIcon = L.divIcon({
      className: 'shape-measurement-label',
      html: measurementHtml,
      iconSize: [100, 30],
      iconAnchor: [50, 15]
    });

    const measurementMarker = L.marker(center, {
      icon: measurementIcon,
      interactive: false,
      zIndexOffset: 2000
    }).addTo(this.map);

    // Store marker reference on the layer for later removal
    if (!layer._measurementMarkers) {
      layer._measurementMarkers = [];
    }
    layer._measurementMarkers.push(measurementMarker);
  }

  /**
   * Clear all drawn shapes
   */
  public clearDrawnShapes(): void {
    if (this.drawnItems) {
      this.drawnItems.clearLayers();
    }
  }

  /**
   * Outline-only vs filled for user-drawn polygons and rectangles on the map.
   */
  public applyPolygonFillVisibility(fillVisible: boolean): void {
    if (!this.drawnItems) {
      return;
    }
    const L = (window as any).L;
    if (!L) {
      return;
    }

    this.drawnItems.eachLayer((layer: any) => {
      if (!(layer instanceof L.Polygon)) {
        return;
      }

      if (fillVisible) {
        const fo =
          layer._osmDrawnStoredFillOpacity !== undefined ? layer._osmDrawnStoredFillOpacity : 0.25;
        const f = layer._osmDrawnStoredFill !== undefined ? layer._osmDrawnStoredFill : true;
        layer.setStyle?.({fillOpacity: fo, fill: f});
        delete layer._osmDrawnStoredFillOpacity;
        delete layer._osmDrawnStoredFill;
      } else {
        if (layer._osmDrawnStoredFillOpacity === undefined) {
          const o = layer.options || {};
          layer._osmDrawnStoredFillOpacity = o.fillOpacity !== undefined ? o.fillOpacity : 0.25;
          layer._osmDrawnStoredFill = o.fill !== false;
        }
        layer.setStyle?.({fillOpacity: 0, fill: true});
      }
    });
  }
}
